import { SIBLING_TYPE, TWIN_TYPE } from './config';

// How a multiple pregnancy's recorded zygosity becomes twin fields — the SINGLE definition, shared by
// the two places that need it so they cannot drift:
//   * deriveReproNodes, stamping the derived FETUS nodes so a multiple draws as a twin group;
//   * the live-birth conversion, stamping the real CHILDREN those fetuses become (decision 18).
// Without the second, converting both twins would silently produce two ordinary siblings and the twin
// relationship recorded during the pregnancy would be lost at the moment of birth.

export const ZYGOSITY_TO_TWIN_TYPE = Object.freeze({
  monozygotic: TWIN_TYPE.monozygotic,
  dizygotic: TWIN_TYPE.dizygotic,
  unknown: TWIN_TYPE.unknown
});

export const SIBLING_TYPE_BY_COUNT = Object.freeze({
  2: SIBLING_TYPE.twin,
  3: SIBLING_TYPE.triplet,
  4: SIBLING_TYPE.quadruplet,
  5: SIBLING_TYPE.quintuplet,
  6: SIBLING_TYPE.sextuplet
});

/**
 * `siblingType` for a multiple birth of `count` people. THE one mapping — TGCLOUD-1692.
 *
 * There were three copies of this before: here, `sibTypeForSize` below, `utils.js`'s
 * `siblingTypeForSize` (XML import) and an inline expression in `deriveReproNodes`. All
 * four shared the same two bugs, which is what having four copies buys you:
 *
 *   * every one ended `|| SIBLING_TYPE.quadruplet`, so a set of FIVE was stored and
 *     displayed as a quadruplet — verified, not theorised;
 *   * a `count` of 0 or 1 hit that same fallback, so "one person" also came back
 *     `quadruplet`. Nothing called it that way, but nothing stopped it either.
 *
 * Below two, there is no multiple birth: a group of one is not a thing, which is the
 * rule `pruneAndRederiveTwins` already enforces by clearing anyone left alone.
 */
export const siblingTypeForCount = (count) => {
  const n = Number(count);
  if (!Number.isFinite(n) || n < 2) return SIBLING_TYPE.normal;
  return SIBLING_TYPE_BY_COUNT[n] || SIBLING_TYPE.multiple;
};

// The twin descriptors for a multiple of `count` siblings with this recorded zygosity. An unrecorded
// zygosity is `unknown`, which Bennett draws as a "?" between the twin lines — the honest default, and
// the convention Tim ruled prevails over Desktop's plain slanted lines.
export const twinFieldsFor = ({ count, zygosity }) => ({
  siblingType: siblingTypeForCount(count),
  twinType: ZYGOSITY_TO_TWIN_TYPE[zygosity] ?? TWIN_TYPE.unknown
});

// A stable-enough group id. Deliberately not derived from the pregnancy `_id`: a carrier can have two
// separate twin pregnancies, and they must not share a twin group.
export const newTwinGroupId = () =>
  `twin-group-${Math.random().toString(36).slice(2, 8)}`;

// Stamp twin membership symmetrically across a set of member pids. Mirrors what addSiblingsToRecords
// does for a twin added by hand: a shared `twinSubGroupId`, the `siblingType`, and `twinRelations`
// naming every OTHER member of the group. `siblingIds` are deliberately untouched — the children of one
// pregnancy already share both parents, so the ordinary sibling edges are maintained by
// addChildrenToRecords, and rewriting them here could clobber half-siblings.
//
// An existing group id among the members wins, so converting a third fetus joins the group the first
// two already formed rather than starting a rival one.
export const linkTwinMembers = (members, pids, { siblingType, twinType }) => {
  const inGroup = (pids || []).map(String);
  if (inGroup.length < 2) return members || [];
  const list = members || [];
  const existingGroupId = list
    .filter((m) => inGroup.includes(String(m.pid)) && m.twinSubGroupId)
    .map((m) => m.twinSubGroupId)[0];
  const groupId = existingGroupId || newTwinGroupId();

  return list.map((m) => {
    if (!inGroup.includes(String(m.pid))) return m;
    const others = inGroup.filter((p) => p !== String(m.pid));
    // Preserve any twin relation to someone OUTSIDE this group (a pre-existing twin), then assert the
    // relation to each member of it.
    const kept = (m.twinRelations || []).filter(
      (r) => !others.includes(String(r?.withPid))
    );
    return {
      ...m,
      twinSubGroupId: groupId,
      siblingType,
      twinRelations: [
        ...kept,
        ...others.map((withPid) => ({
          // Numeric where the pid is numeric, matching how the rest of the graph stores it.
          withPid: Number.isNaN(Number(withPid)) ? withPid : Number(withPid),
          type: twinType
        }))
      ]
    };
  });
};

// ---------------------------------------------------------------------------
// Un-twinning (TGCLOUD-1643)
// ---------------------------------------------------------------------------

/**
 * A detached or un-twinned node is no longer a twin of anyone.
 *
 * Exported because `EdgeContextMenu` applies it directly to the child it is
 * detaching, as well as through `pruneAndRederiveTwins`.
 */
export const clearTwinData = (m) => ({
  ...m,
  twinRelations: [],
  twinSubGroupId: null,
  siblingType: SIBLING_TYPE.normal,
  // TGCLOUD-1697. The LEGACY questionnaire fields are cleared too, and they have to be.
  // The pedigree tool never writes `isTwin` (TGCLOUD-1692 removed it as derived), so these
  // only ever arrive from the questionnaire — and they are what `validatePedigreeConfig`
  // reconstructs from, and what the FHQ apply now rebuilds config twin sets from. Leaving
  // them set turns a deliberate un-twin into a temporary one: the next apply, or the next
  // render of a family whose config was lost, silently re-asserts the relationship the
  // clinician removed, zygosity and all. Same convention proband-info/probandUtil.jsx uses.
  isTwin: 'false',
  twinMember: null,
  twinType: null
});

const sibTypeForSize = siblingTypeForCount;

/**
 * Prune every twin relation pointing at `goingPids`, then re-derive the groups that
 * remain.
 *
 * Lifted VERBATIM out of `EdgeContextMenu`'s `pruneAndRederiveTwins`, which was the
 * only implementation of this and served only the "a child was detached from the
 * sibship" case. TGCLOUD-1643 needs the same operation for a deliberate un-twin, and
 * a third hand-rolled copy is how the two come to disagree about what a triplet
 * minus one is.
 *
 * Three things it does, in order:
 *
 * 1. **Bridges the survivors.** A group is not guaranteed to be a complete graph —
 *    `linkTwinMembers` builds one, but hand-added and pregnancy-derived groups may be
 *    chained — so partners of a departing member are joined to each other or a
 *    triplet losing one would fall apart entirely instead of becoming a pair.
 * 2. **Prunes** the relations to the departing pids.
 * 3. **Re-derives** membership with union-find over MUTUAL relations only, and sets
 *    `siblingType` from the resulting group size. Anyone left with fewer than two is
 *    cleared outright.
 *
 * NOTE the bridge asserts `dizygotic` rather than carrying the departing member's
 * recorded zygosity across. That is the pre-existing behaviour and is preserved here
 * deliberately: this extraction is meant to be behaviour-identical. It is arguably
 * wrong — if A and C were both monozygotic with B, then A and C are monozygotic with
 * each other — and is worth its own ticket rather than a silent change inside a
 * refactor.
 *
 * @param {Array}  siblingMembers members to consider (a sibship, or the whole list)
 * @param {Array}  goingPids      pids whose twin relations are being severed
 */
export const pruneAndRederiveTwins = (siblingMembers, goingPids) => {
  const removed = new Set(goingPids);

  const working = (siblingMembers || []).map((m) => ({
    ...m,
    twinRelations: (m.twinRelations || []).map((r) => ({ ...r }))
  }));
  const byPid = new Map(working.map((m) => [m.pid, m]));
  (goingPids || []).forEach((rpid) => {
    const partners = working
      .filter((m) => m.twinRelations.some((r) => r.withPid === rpid))
      .map((m) => m.pid)
      .sort((a, b) => a - b);
    for (let i = 0; i < partners.length - 1; i++) {
      const a = byPid.get(partners[i]);
      const b = byPid.get(partners[i + 1]);
      const alreadyLinked = a.twinRelations.some((r) => r.withPid === b.pid);
      if (!alreadyLinked) {
        a.twinRelations.push({ withPid: b.pid, type: TWIN_TYPE.dizygotic });
        b.twinRelations.push({ withPid: a.pid, type: TWIN_TYPE.dizygotic });
      }
    }
  });

  const pruned = working.map((m) => ({
    ...m,
    twinRelations: m.twinRelations.filter((r) => !removed.has(r.withPid))
  }));

  const ufParent = new Map();
  const find = (x) => {
    if (!ufParent.has(x)) ufParent.set(x, x);
    if (ufParent.get(x) !== x) ufParent.set(x, find(ufParent.get(x)));
    return ufParent.get(x);
  };
  const union = (a, b) => ufParent.set(find(a), find(b));
  const prunedByPid = new Map(pruned.map((m) => [m.pid, m]));
  pruned.forEach((m) => {
    m.twinRelations.forEach((r) => {
      const other = prunedByPid.get(r.withPid);
      const mutual =
        other && other.twinRelations.some((o) => o.withPid === m.pid);
      if (mutual) union(m.pid, r.withPid);
    });
  });

  const groupSize = new Map();
  pruned.forEach((m) => {
    if (!m.twinRelations.length) return;
    const root = find(m.pid);
    groupSize.set(root, (groupSize.get(root) || 0) + 1);
  });

  return pruned.map((m) => {
    const hasLinks = m.twinRelations.length > 0;
    const root = find(m.pid);
    const size = hasLinks ? groupSize.get(root) || 0 : 0;
    if (hasLinks && size >= 2) {
      return { ...m, twinSubGroupId: root, siblingType: sibTypeForSize(size) };
    }
    return clearTwinData(m);
  });
};

/**
 * Take `pids` out of whatever twin group they are in, leaving them as ordinary
 * siblings and shrinking the group behind them.
 *
 * The inverse of `linkTwinMembers`, and the difference from the removal case is that
 * these people STAY on the pedigree — so they are cleared themselves as well as
 * pruned from everyone else. A triplet losing one member becomes a pair; a pair
 * losing one leaves both as singletons, because a twin group of one is not a thing.
 *
 * `siblingIds` are untouched, exactly as in `linkTwinMembers`: un-twinning changes
 * how the birth is drawn, not who anyone's siblings are.
 *
 * SCOPED TO THE AFFECTED GROUP, deliberately. `pruneAndRederiveTwins` normalises
 * every member it is handed — anyone without twin links comes back carrying
 * `twinSubGroupId: null`, `siblingType: 'normal'`, `twinRelations: []`. That is
 * harmless when it is fed one sibship, which is how `EdgeContextMenu` calls it, but
 * feeding it a whole pedigree would stamp twin fields onto every unrelated person on
 * the chart — a pile of spurious writes on the next save, and it would overwrite the
 * `siblingType` of derived reproductive nodes. So the closure of the twin graph
 * around the leavers is computed first and only those members are rewritten.
 */
export const unlinkTwinMembers = (members, pids) => {
  const going = (pids || []).map(Number).filter((p) => !Number.isNaN(p));
  if (!going.length) return members || [];
  const list = members || [];
  const byPid = new Map(list.map((m) => [m.pid, m]));

  // Transitive closure over twinRelations from the leavers: a triplet's third member
  // is reachable only through the second, and re-deriving a group without all of it
  // present would mis-size it.
  const affected = new Set();
  const walk = (pid) => {
    if (affected.has(pid)) return;
    const member = byPid.get(pid);
    if (!member) return;
    affected.add(pid);
    (member.twinRelations || []).forEach((r) => walk(r?.withPid));
  };
  going.forEach(walk);

  const rederived = pruneAndRederiveTwins(
    list.filter((m) => affected.has(m.pid)),
    going
  );
  const goingSet = new Set(going);
  const updated = new Map(
    rederived.map((m) => [m.pid, goingSet.has(m.pid) ? clearTwinData(m) : m])
  );

  return list.map((m) => updated.get(m.pid) || m);
};

// ---------------------------------------------------------------------------
// Reading twin status, and who may join a group (TGCLOUD-1643)
// ---------------------------------------------------------------------------

/**
 * Display wording for a zygosity, matching the context menu's Brother/Sister flyout
 * exactly (renamed under TGCLOUD-1644). Shared so the sidebar and the menu cannot
 * describe the same stored value differently.
 *
 * The STORED values are untouched — `monozygotic` / `dizygotic` / `unknown` are what
 * `path.js` and this module read.
 */
export const TWIN_TYPE_LABELS = Object.freeze({
  [TWIN_TYPE.monozygotic]: 'Identical twin',
  [TWIN_TYPE.dizygotic]: 'Non-identical twin',
  [TWIN_TYPE.unknown]: 'Zygosity unknown'
});

export const NOT_A_TWIN_LABEL = 'Not a twin';

/**
 * The CONFIRMED twin group `member` belongs to: the people they have a mutual twin
 * relation with whose `type` agrees on both sides.
 *
 * Mutual-and-matching is not pedantry — it is the render contract. `path.js` draws a
 * twin line only for a pair that agrees on both sides, so a one-sided or
 * type-mismatched relation draws nothing at all. Reporting it as a twin here would
 * tell the clinician something the chart flatly contradicts, which is exactly the
 * "I marked them and nothing happened" confusion.
 *
 * @returns {{ partners: Array, twinType: ?string }} partners is empty when not a twin
 */
export const twinGroupOf = (member, members) => {
  const list = members || [];
  const mine = member?.twinRelations || [];
  const partners = mine
    .map((r) => ({
      other: list.find((m) => m.pid === r?.withPid),
      type: r?.type
    }))
    .filter(
      ({ other, type }) =>
        other &&
        (other.twinRelations || []).some(
          (o) => o?.withPid === member.pid && o?.type === type
        )
    );

  return {
    partners: partners.map(({ other }) => other),
    // One group has one zygosity; the first confirmed relation defines it.
    twinType: partners[0]?.type ?? null
  };
};

/**
 * Who this person could be made a twin OF.
 *
 * FULL siblings only — same father AND same mother. `linkTwinMembers` does not
 * validate this and half-siblings cannot be twins by definition, so the restriction
 * has to live at the point of choosing. A person with fewer than two recorded parents
 * has no candidates at all, because "same parents" cannot be established.
 *
 * Excludes anyone already in a DIFFERENT confirmed twin group: joining them would
 * silently merge two groups. Members of the person's OWN group are excluded too —
 * they are already twins, so there is nothing to offer.
 */
export const twinCandidatesFor = (member, members) => {
  const list = members || [];
  const fatherId = member?.fatherId;
  const motherId = member?.motherId;
  if (fatherId == null || motherId == null) return [];

  const ownGroup = new Set(
    twinGroupOf(member, list).partners.map((p) => p.pid)
  );

  return list.filter((m) => {
    if (!m || m.pid === member.pid) return false;
    if (m.fatherId !== fatherId || m.motherId !== motherId) return false;
    if (ownGroup.has(m.pid)) return false;
    // In a confirmed group of their own, with people other than this member.
    const theirGroup = twinGroupOf(m, list).partners;
    return !theirGroup.length;
  });
};

// ---------------------------------------------------------------------------
// Zygosity components and runs (TGCLOUD-1692)
// ---------------------------------------------------------------------------

/** Pids compare loosely: stored as Number, but hand-built fixtures and the XML
 *  import path both produce strings. `path.js` used `===` and so silently drew
 *  nothing for a mixed-type pair. */
const samePid = (a, b) => String(a) === String(b);

/** Numeric where both sides are numeric, lexicographic otherwise — so 10 sorts after 3
 *  rather than before it. Shared, because two callers need the SAME deterministic order:
 *  component ordering, and choosing which existing group id a merge keeps. */
export const comparePidLike = (a, b) => {
  const an = Number(a);
  const bn = Number(b);
  if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
  return String(a).localeCompare(String(b));
};

/**
 * Is this pair CONFIRMED — and, when `type` is given, confirmed AT that zygosity?
 *
 * THE definition of the render contract, extracted so that the renderer, the
 * ordering pass and the sidebar all answer this question the same way. Before
 * TGCLOUD-1692 it existed twice: inline in `path.js` as `isConfirmedPair`, and again
 * inside `twinGroupOf`'s filter. A set is only drawn the way it is described if those
 * two agree.
 *
 * MUTUAL AND TYPE-MATCHED ON BOTH SIDES. A one-sided relation, or one where the two
 * sides disagree about the zygosity, is not a pair — it draws nothing, and it must
 * therefore not be *described* as anything either. That asymmetry is the "I marked
 * them and nothing happened" symptom.
 *
 * @param {?string} type zygosity to require, or null/undefined for "any confirmed"
 */
export const isConfirmedTwinPair = (a, b, type) => {
  if (!a || !b || samePid(a.pid, b.pid)) return false;
  const ab = (a.twinRelations || []).find((r) => samePid(r?.withPid, b.pid));
  const ba = (b.twinRelations || []).find((r) => samePid(r?.withPid, a.pid));
  if (!ab || !ba || ab.type !== ba.type) return false;
  return type == null || ab.type === type;
};

/**
 * Connected components of the subgraph of confirmed pairs of ONE zygosity.
 *
 * A set of three or more does not have "a zygosity" — `twinRelations[].type` is per
 * PAIR, and the clinically common triplet is one monozygotic pair alongside a third
 * who is not (Tim's decision, 20 Aug 2026). So "which people here are identical to
 * each other" is a question about components of a subgraph, not a field.
 *
 * Components of one are dropped: a person is not identical to themselves in any sense
 * the pedigree draws.
 *
 * Deterministic ordering — pids ascending within a component, components by their
 * lowest pid — because two of the three consumers feed geometry, and a set whose
 * rendering depends on array order would move about between loads.
 *
 * @returns {Array<Array>} e.g. [[1, 2], [4, 5]] — pids, in each member's own type
 */
export const zygosityComponents = (members, type) => {
  const list = (members || []).filter((m) => m && m.pid != null);
  const parent = new Map();
  const find = (x) => {
    if (!parent.has(x)) parent.set(x, x);
    if (parent.get(x) !== x) parent.set(x, find(parent.get(x)));
    return parent.get(x);
  };
  const union = (a, b) => parent.set(find(a), find(b));

  for (let i = 0; i < list.length; i += 1) {
    for (let j = i + 1; j < list.length; j += 1) {
      if (isConfirmedTwinPair(list[i], list[j], type)) {
        union(String(list[i].pid), String(list[j].pid));
      }
    }
  }

  const byRoot = new Map();
  list.forEach((m) => {
    const key = String(m.pid);
    if (!parent.has(key)) return; // in no pair of this zygosity
    const root = find(key);
    if (!byRoot.has(root)) byRoot.set(root, []);
    byRoot.get(root).push(m.pid);
  });

  return [...byRoot.values()]
    .filter((g) => g.length > 1)
    .map((g) => [...g].sort(comparePidLike))
    .sort((a, b) => comparePidLike(a[0], b[0]));
};

/**
 * Maximal runs of members that are BOTH in one zygosity component AND consecutive in
 * `orderedPids`. A run is the largest span a single glyph may legitimately cover.
 *
 * This is the fix for the defect that motivated TGCLOUD-1692. `twinGroupPath` used to
 * emit one monozygotic bar per PAIR at a single y, with no adjacency test — so a
 * trizygotic triplet containing one identical pair at the two OUTER positions drew a
 * bar straight across the middle sibling's descent line, and the chart asserted that
 * all three were identical. Verified on the real renderer, not theorised.
 *
 * Drawing per run instead makes the failure mode an OMISSION rather than a false
 * claim: a component whose members are not adjacent yields no run, so no bar. The
 * ordering pass (`orderSiblings`) is what stops that happening; this is what makes it
 * safe when ordering cannot — a locked `establishedOrder`, say.
 *
 * @param {Array} members     the set's members, carrying twinRelations
 * @param {Array} orderedPids the set's pids in the order they are DRAWN, left to right
 * @param {?string} type      zygosity whose components to run over
 */
export const zygosityRuns = (members, orderedPids, type) => {
  const components = zygosityComponents(members, type);
  const componentOf = new Map();
  components.forEach((group, index) => {
    group.forEach((pid) => componentOf.set(String(pid), index));
  });

  const runs = [];
  let run = [];
  const flush = () => {
    if (run.length > 1) runs.push(run);
    run = [];
  };

  (orderedPids || []).forEach((pid) => {
    const component = componentOf.get(String(pid));
    if (component == null) {
      flush();
      return;
    }
    const previous = run.length
      ? componentOf.get(String(run[run.length - 1]))
      : null;
    if (previous === component) run.push(pid);
    else {
      flush();
      run = [pid];
    }
  });
  flush();

  return runs;
};

// ---------------------------------------------------------------------------
// Joining an existing set, without flattening what it already records
// ---------------------------------------------------------------------------

/**
 * A stable, order-independent key for one pair. Exported so the sidebar can hand
 * `joinTwinSet` per-pair overrides without either side inventing its own convention.
 */
export const twinPairKey = (a, b) => [String(a), String(b)].sort().join('|');

/**
 * The set `pid` belongs to.
 *
 * TWO signals, unioned, because either can be missing on real data:
 *
 *   * a shared `twinSubGroupId` — membership rather than confirmed pairs, so a chained or
 *     half-written set is still recognised as a set, which is what lets it be repaired;
 *   * the transitive closure of `twinRelations` — because a set can carry relations and
 *     NO group id at all. That is not hypothetical: it is what the pre-1692 sidebar wrote
 *     for the very first pair it created, and relying on the group id alone silently
 *     reduced such a set to the anchor. A "join" would then have dropped the existing
 *     partner and re-labelled a triplet as a twin, and a zygosity correction would have
 *     been a no-op.
 *
 * The closure walks relations regardless of whether they are mutual, deliberately: a
 * one-sided relation is a set member whose record needs fixing, not a stranger.
 */
const setMembersOf = (list, pid) => {
  const byKey = new Map((list || []).map((m) => [String(m?.pid), m]));
  const anchor = byKey.get(String(pid));
  if (!anchor) return [];

  const found = new Set();
  const walk = (key) => {
    if (found.has(key)) return;
    const member = byKey.get(key);
    if (!member) return;
    found.add(key);
    (member.twinRelations || []).forEach((r) => walk(String(r?.withPid)));
    if (member.twinSubGroupId) {
      (list || []).forEach((m) => {
        if (m?.twinSubGroupId === member.twinSubGroupId) walk(String(m.pid));
      });
    }
  };
  walk(String(pid));

  return (list || []).filter((m) => found.has(String(m?.pid)));
};

/**
 * Add people to a multiple birth, asserting `twinType` only for the pairs that are
 * actually new (TGCLOUD-1692).
 *
 * `linkTwinMembers` stamps ONE zygosity across every pair in the set. That is right
 * where the set genuinely has one — a pregnancy record carries a single `zygosity`
 * field, which is why the live-birth path uses it — but it is destructive here. Once
 * zygosity is per pair, adding a fourth co-multiple "as an identical twin" through
 * `linkTwinMembers` would rewrite the existing three pairs to monozygotic as well,
 * silently discarding what a clinician had recorded.
 *
 * So: every pair involving a joiner gets `twinType`; every pair that already agrees on
 * both sides is left exactly as it is; and pairs that are missing altogether are filled
 * in, which is what repairs a chained set.
 *
 * HOW A MISSING PAIR IS FILLED, and why it is not arbitrary. Monozygotic is transitive
 * — co-multiples from one zygote are all identical to one another — so if A–B and A–C
 * are both monozygotic then B–C is monozygotic too, and asserting it is stating a fact
 * the record already contains. Dizygotic is NOT transitive (A being non-identical to
 * both B and C says nothing about B and C), so anything else fills in as `unknown`.
 * That is a deliberate improvement on `pruneAndRederiveTwins`, whose bridge asserts
 * `dizygotic` — behaviour its own comment flags as "arguably wrong" and which was left
 * alone there because that extraction had to be behaviour-identical.
 *
 * This is also the repair for the defect that the context menu used to create: adding a
 * third co-multiple wrote a relation only to the person right-clicked, leaving the other
 * two with none to each other and the whole set labelled `twin`. Because
 * `twinCandidatesFor` excludes anyone already in a confirmed group, that state could not
 * be fixed from the sidebar either.
 *
 * @param {Array}  members  the whole member list
 * @param {*}      anchorPid someone already in the set (or the person being twinned)
 * @param {Array}  joining  pids being added
 * @param {string} twinType zygosity to assert for the new pairs
 * @param {?Map}   pairTypes optional per-pair overrides, keyed by `twinPairKey`. Consulted
 *   FIRST, so an override can also CORRECT an existing pair — which is how the sidebar
 *   edits one relationship of a set without disturbing the others.
 */
export const joinTwinSet = (
  members,
  anchorPid,
  joining,
  twinType,
  pairTypes
) => {
  const list = members || [];
  const joiningKeys = (joining || []).map(String);
  const overrides = pairTypes instanceof Map ? pairTypes : new Map();
  // Nothing to add AND nothing to correct.
  if (!joiningKeys.length && !overrides.size) return list;

  // THE CLOSURE IS WALKED FROM THE JOINERS AS WELL AS THE ANCHOR. Walking it from the
  // anchor alone orphaned whatever set a joiner was already in, and that is not a tidiness
  // problem — it silently un-draws a recorded relationship.
  //
  // Four full siblings as two identical pairs, {3,4} and {5,6}. Select 3, tick 5 only, save.
  // An anchor-only closure gives {3,4,5}; 6 keeps its old group id and a still-confirmed
  // monozygotic relation to 5. Its sub-group now has ONE member, so `layout.js`'s
  // `twins.length > 1` emits no twin-group edge at all: pair 5–6 loses both diagonals AND
  // its bar, 6 draws as an ordinary sibling — while 6's own card still reads "Identical twin
  // of UPN 5". Chart and sidebar contradicting each other on a genetic fact is exactly what
  // the confirmed-pair contract exists to prevent.
  //
  // It was also not idempotent: the second identical save reached 6 through 5's retained
  // relation and produced a quadruplet, so one click and two clicks gave different records.
  // Ticking one of two offered people is an ordinary action — the RTL test missed it only
  // because it ticks every checkbox.
  const closure = [String(anchorPid), ...joiningKeys].flatMap((key) =>
    setMembersOf(list, key).map((m) => String(m.pid))
  );
  const setKeys = [...new Set([...closure, ...joiningKeys])].filter((key) =>
    list.some((m) => samePid(m?.pid, key))
  );
  if (setKeys.length < 2) return list;

  const inSet = list.filter((m) => setKeys.includes(String(m.pid)));
  // The ANCHOR's group id wins, then the lowest pid's. Taking the first id in member-array
  // order made the outcome depend on array position: with a joiner ahead of the anchor a
  // different id won and a different set of pairs was written, leaving `twinSetIsComplete`
  // false immediately after a successful save — so the card read "This set is not fully
  // recorded" about a set that had just been fully recorded.
  const anchorGroupId = inSet.find((m) =>
    samePid(m?.pid, anchorPid)
  )?.twinSubGroupId;
  const groupId =
    anchorGroupId ||
    [...inSet]
      .filter((m) => m.twinSubGroupId)
      .sort((a, b) => comparePidLike(a.pid, b.pid))[0]?.twinSubGroupId ||
    newTwinGroupId();

  // Computed BEFORE anything is written, so transitivity is judged on what the record
  // actually said rather than on what this call is about to assert.
  const identicalComponent = new Map();
  zygosityComponents(inSet, TWIN_TYPE.monozygotic).forEach((group, index) => {
    group.forEach((pid) => identicalComponent.set(String(pid), index));
  });

  const byKey = new Map(inSet.map((m) => [String(m.pid), m]));
  const typeFor = (a, b) => {
    // An explicit choice wins over everything, including a pair already recorded — this
    // is the "correct the zygosity" path.
    const override = overrides.get(twinPairKey(a, b));
    if (override) return override;
    const held = (byKey.get(a)?.twinRelations || []).find((r) =>
      samePid(r?.withPid, b)
    );
    if (isConfirmedTwinPair(byKey.get(a), byKey.get(b))) return held.type;
    if (joiningKeys.includes(a) || joiningKeys.includes(b)) return twinType;
    // A one-sided relation is mirrored rather than replaced — somebody recorded it.
    if (held?.type) return held.type;
    const mirrored = (byKey.get(b)?.twinRelations || []).find((r) =>
      samePid(r?.withPid, a)
    );
    if (mirrored?.type) return mirrored.type;
    const ca = identicalComponent.get(a);
    const cb = identicalComponent.get(b);
    return ca != null && ca === cb ? TWIN_TYPE.monozygotic : TWIN_TYPE.unknown;
  };

  const asStoredPid = (key) => (Number.isNaN(Number(key)) ? key : Number(key));
  const siblingType = siblingTypeForCount(setKeys.length);

  return list.map((m) => {
    const key = String(m?.pid);
    if (!setKeys.includes(key)) return m;
    const others = setKeys.filter((k) => k !== key);
    // Relations to people OUTSIDE this set survive — somebody can be twinned with
    // someone the set does not contain (a pregnancy-derived co-fetus, say).
    const kept = (m.twinRelations || []).filter(
      (r) => !others.includes(String(r?.withPid))
    );
    return {
      ...m,
      twinSubGroupId: groupId,
      siblingType,
      twinRelations: [
        ...kept,
        ...others.map((other) => ({
          withPid: asStoredPid(other),
          type: typeFor(key, other)
        }))
      ]
    };
  });
};

/**
 * Is every pair in this person's set mutually recorded?
 *
 * A set is drawn from `twinSubGroupId` but a LINE is drawn only for a confirmed pair, so
 * a set can be complete as membership and incomplete as relationships — which is exactly
 * what the old context menu produced (a relation to the person right-clicked and to
 * nobody else). The sidebar uses this to offer a save that repairs it; without it, Save
 * was gated on a change and such a set offered no candidates either, so it could not be
 * corrected at all.
 */
export const twinSetIsComplete = (member, members) => {
  const list = members || [];
  const set = setMembersOf(list, member?.pid);
  if (set.length < 2) return true;
  for (let i = 0; i < set.length; i += 1) {
    for (let j = i + 1; j < set.length; j += 1) {
      if (!isConfirmedTwinPair(set[i], set[j])) return false;
    }
  }
  return true;
};

/**
 * Full siblings who are already in a DIFFERENT confirmed set.
 *
 * `twinCandidatesFor` excludes them on purpose — adding one MERGES two sets, and doing
 * that silently would be worse than not offering it. But not offering it at all meant two
 * recorded pairs among four full siblings could never be corrected to quadruplets, which
 * is a real and unreachable state. They are offered separately so the UI can say what
 * will happen (TGCLOUD-1692).
 */
export const twinMergeCandidatesFor = (member, members) => {
  const list = members || [];
  const fatherId = member?.fatherId;
  const motherId = member?.motherId;
  if (fatherId == null || motherId == null) return [];

  const ownGroup = new Set(
    twinGroupOf(member, list).partners.map((p) => p.pid)
  );

  return list.filter((m) => {
    if (!m || m.pid === member.pid) return false;
    if (m.fatherId !== fatherId || m.motherId !== motherId) return false;
    if (ownGroup.has(m.pid)) return false;
    // The inverse of twinCandidatesFor's last test: in a confirmed set of their own.
    return twinGroupOf(m, list).partners.length > 0;
  });
};
